/**
 * 预定义人群分类常量
 * 用于模型注册时的 population_tags 选择和模型匹配时的患者分类
 */

import type { PopulationGroup } from './populationGroups';

export interface PopulationCategory {
  key: string;
  label: string;
  labelEn: string;
  /** 典型年龄描述 */
  ageHint: string;
  /** 父级人群标签（支持层级关系） */
  parent?: string;
}

export const POPULATION_CATEGORIES: PopulationCategory[] = [
  { key: '极早产儿', label: '极早产儿', labelEn: 'Extremely preterm', ageHint: 'GA <28w', parent: '新生儿' },
  { key: '早产儿', label: '早产儿', labelEn: 'Preterm', ageHint: 'GA 28-36w', parent: '新生儿' },
  { key: '足月新生儿', label: '足月新生儿', labelEn: 'Term neonate', ageHint: 'GA ≥37w, PNA <28d', parent: '新生儿' },
  { key: '新生儿', label: '新生儿', labelEn: 'Neonate', ageHint: 'PNA <28d', parent: '儿科人群' },
  { key: '婴儿', label: '婴儿', labelEn: 'Infant', ageHint: '1m - 1y', parent: '儿科人群' },
  { key: '幼儿', label: '幼儿', labelEn: 'Toddler', ageHint: '1 - 3y', parent: '儿科人群' },
  { key: '儿童', label: '儿童', labelEn: 'Child', ageHint: '3 - 12y', parent: '儿科人群' },
  { key: '青少年', label: '青少年', labelEn: 'Adolescent', ageHint: '12 - 18y', parent: '儿科人群' },
  { key: '儿科人群', label: '儿科人群', labelEn: 'Pediatric', ageHint: '<18y' },
  { key: '成人', label: '成人', labelEn: 'Adult', ageHint: '18 - 65y' },
  { key: '老年', label: '老年', labelEn: 'Elderly', ageHint: '>65y', parent: '成人' },
];

/** Select options for Ant Design */
export const POPULATION_TAG_OPTIONS = POPULATION_CATEGORIES.map((c) => ({
  label: `${c.label} (${c.labelEn}, ${c.ageHint})`,
  value: c.key,
}));

/** 获取人群标签的层级链（包括自身和所有父级） */
export function getPopulationHierarchy(key: string): string[] {
  const hierarchy: string[] = [key];
  let current = POPULATION_CATEGORIES.find(c => c.key === key);

  while (current?.parent) {
    hierarchy.push(current.parent);
    current = POPULATION_CATEGORIES.find(c => c.key === current!.parent);
  }

  return hierarchy;
}

/** 获取人群标签的显示名称 */
export function getPopulationLabel(key: string): string {
  const cat = POPULATION_CATEGORIES.find(c => c.key === key);
  return cat ? `${cat.label} (${cat.labelEn})` : key;
}

export function getPopulationTagsForGroup(group?: PopulationGroup | null): string[] {
  switch (group) {
    case 'neonate':
      return ['极早产儿', '早产儿', '足月新生儿', '新生儿'];
    case 'pediatric':
      return ['极早产儿', '早产儿', '足月新生儿', '新生儿', '婴儿', '幼儿', '儿童', '青少年', '儿科人群'];
    case 'adult':
      return ['成人', '老年'];
    default:
      return [];
  }
}

const AGE_CONFLICT_TOLERANCE_YEARS = 0.25;

function toFiniteNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function dedupePopulationTags(tags: Array<string | null | undefined>): string[] {
  return Array.from(new Set(tags.filter((tag): tag is string => Boolean(tag))));
}

export function classifyPatientPopulationTags(values: Record<string, unknown>): string[] {
  const tags: string[] = [];
  let gestationalAge = toFiniteNumber(values.gestational_age);
  let agePna = toFiniteNumber(values.age_pna);
  const ageYearsExplicit = toFiniteNumber(values.age_years);

  if (ageYearsExplicit !== null && agePna !== null) {
    const derivedAgeYears = agePna / 365.25;
    if (
      ageYearsExplicit >= 1
      && Math.abs(ageYearsExplicit - derivedAgeYears) > AGE_CONFLICT_TOLERANCE_YEARS
    ) {
      gestationalAge = null;
      agePna = null;
    }
  }

  if (gestationalAge !== null) {
    if (gestationalAge < 28) {
      tags.push('极早产儿');
    } else if (gestationalAge < 37) {
      tags.push('早产儿');
    } else if (agePna !== null && agePna < 28) {
      tags.push('足月新生儿');
    }
  }

  if (tags.length > 0) {
    return dedupePopulationTags([...tags, '新生儿', '儿科人群']);
  }

  if (agePna !== null) {
    const ageMonths = agePna / 30.44;
    const ageYears = agePna / 365.25;

    if (agePna < 28) {
      tags.push('新生儿', '儿科人群');
    } else if (ageMonths >= 1 && ageMonths < 12) {
      tags.push('婴儿', '儿科人群');
    } else if (ageYears >= 1 && ageYears < 3) {
      tags.push('幼儿', '儿科人群');
    } else if (ageYears >= 3 && ageYears < 12) {
      tags.push('儿童', '儿科人群');
    } else if (ageYears >= 12 && ageYears < 18) {
      tags.push('青少年', '儿科人群');
    } else if (ageYears >= 18 && ageYears < 65) {
      tags.push('成人');
    } else if (ageYears >= 65) {
      tags.push('老年', '成人');
    }
  }

  if (ageYearsExplicit !== null && tags.length === 0) {
    if (ageYearsExplicit < 1) {
      tags.push('婴儿', '儿科人群');
    } else if (ageYearsExplicit < 3) {
      tags.push('幼儿', '儿科人群');
    } else if (ageYearsExplicit < 12) {
      tags.push('儿童', '儿科人群');
    } else if (ageYearsExplicit < 18) {
      tags.push('青少年', '儿科人群');
    } else if (ageYearsExplicit < 65) {
      tags.push('成人');
    } else {
      tags.push('老年', '成人');
    }
  }

  return dedupePopulationTags(tags);
}

export function matchPopulationTags(
  patientTags: string[],
  ruleTags?: string[] | null,
): boolean {
  if (!ruleTags || ruleTags.length === 0) {
    return true;
  }

  const expandedPatientTags = new Set<string>();
  patientTags.forEach((tag) => {
    getPopulationHierarchy(tag).forEach((hierarchyTag) => expandedPatientTags.add(hierarchyTag));
  });

  return ruleTags.some((tag) => expandedPatientTags.has(tag));
}

